
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/SEO';

interface FAQItem {
  question: string;
  answer: string;
  blogLink?: { text: string; slug: string };
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs: FAQItem[] = [
    {
      question: 'Hvordan finder jeg den rigtige sadel til min hest?',
      answer: 'At vælge den rigtige sadel kræver omhyggelig overvejelse af både hestens og rytterens behov. Vi har skrevet en omfattende guide om dette emne.',
      blogLink: {
        text: 'Læs vores guide: Sådan Vælger Du Den Rigtige Sadel',
        slug: '/blog/saadan-vaelger-du-den-rigtige-sadel'
      }
    },
    {
      question: 'Hvordan fungerer jeres affiliate model?',
      answer: 'EquiNord fungerer som en kurateret platform hvor vi forbinder dig med pålidelige hesteforhandlere. Når du klikker på et produkt og køber hos vores partnere, tjener vi en lille kommission - uden ekstra omkostninger for dig. Dette giver os mulighed for at drive platformen og fortsætte med at finde de bedste produkter til hesteelskere.'
    },
    {
      question: 'Er produkterne dyrere når jeg køber gennem EquiNord?',
      answer: 'Nej! Du betaler aldrig mere ved at købe gennem vores links. Priserne er de samme som ved direkte køb hos forhandleren. Vores provision kommer fra forhandleren, ikke fra dig som kunde.'
    },
    {
      question: 'Kan jeg returnere produkter købt gennem EquiNord?',
      answer: 'Da du køber direkte fra vores partnerforhandlere, gælder deres individuelle returpolitikker. De fleste af vores partnere tilbyder 30-dages returret. Tjek altid den specifikke forhandlers politik før køb.'
    },
    {
      question: 'Hvor ofte opdaterer I jeres produktkataloget?',
      answer: 'Vi opdaterer vores produktfeed dagligt for at sikre at du altid ser de nyeste produkter og korrekte priser fra vores partnere. Produkter der ikke længere er tilgængelige fjernes automatisk.'
    },
    {
      question: 'Hvordan ved jeg om et produkt er på lager?',
      answer: 'Vi viser lagerstatus baseret på information fra vores partnere. Dog opdateres lagerbeholdning løbende hos forhandlerne, så vi anbefaler altid at tjekke direkte hos forhandleren før større køb.'
    },
    {
      question: 'Tilbyder I vejledning om pleje af mit udstyr?',
      answer: 'Ja! Vi har en omfattende blog med guides om alt fra sadelpasning til hovpleje.',
      blogLink: {
        text: 'Besøg vores blog for guides og tips',
        slug: '/blog'
      }
    },
    {
      question: 'Hvem står bag EquiNord?',
      answer: 'EquiNord er grundlagt af passionerede hestefolk med årtiers erfaring inden for ridning, træning og hestevedligeholdelse. Vores team kuraterer omhyggeligt hvert produkt vi fremhæver.',
      blogLink: {
        text: 'Læs mere om os',
        slug: '/about'
      }
    },
    {
      question: 'Hvordan vælger I jeres partnere?',
      answer: 'Vi samarbejder kun med velrenommerede brands og forhandlere kendt for kvalitet, pålidelig kundeservice og fair priser. Alle partnere gennemgår en omhyggelig vurderingsproces før vi fremhæver deres produkter.'
    },
    {
      question: 'Kan jeg kontakte forhandleren direkte?',
      answer: 'Absolut! Vi opfordrer dig til at kontakte forhandleren direkte hvis du har specifikke spørgsmål om et produkt. Kontaktinformationen findes typisk på forhandlerens hjemmeside.'
    },
    {
      question: 'Hvad skal jeg gøre hvis jeg får et defekt produkt?',
      answer: 'Kontakt straks den forhandler du købte fra. De håndterer alle garantisager og produktproblemer direkte. Gem altid din købskvittering.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Ofte Stillede Spørgsmål (FAQ) - Alt Om EquiNord"
        description="Find svar på ofte stillede spørgsmål om hesteudstyr, vores affiliate model, returneringer, priser og meget mere. Få hjælp til dine spørgsmål."
        keywords="faq, ofte stillede spørgsmål, hesteudstyr spørgsmål, sadel vejledning, equinord hjælp"
      />
      <Header />
      <main>
      
      {/* Hero Section */}
      <section className="bg-emerald-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Ofte Stillede Spørgsmål</h1>
          <p className="text-xl opacity-90">
            Find svar på de mest almindelige spørgsmål om EquiNord og vores tjenester
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-md overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                  aria-expanded={openIndex === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <span className="font-semibold text-gray-900 pr-8">
                    {faq.question}
                  </span>
                  <i
                    className={`ri-arrow-${openIndex === index ? 'up' : 'down'}-s-line text-emerald-600 text-xl flex-shrink-0`}
                  ></i>
                </button>
                
                {openIndex === index && (
                  <div id={`faq-answer-${index}`} role="region" className="px-6 pb-5 pt-2">
                    <p className="text-gray-700 leading-relaxed mb-3">
                      {faq.answer}
                    </p>
                    {faq.blogLink && (
                      <Link
                        to={faq.blogLink.slug}
                        className="inline-flex items-center text-emerald-600 hover:text-emerald-700 font-medium"
                      >
                        {faq.blogLink.text}
                        <i className="ri-arrow-right-line ml-2"></i>
                      </Link>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Still Have Questions */}
          <div className="mt-16 bg-emerald-50 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Har du stadig spørgsmål?
            </h2>
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
              Kunne du ikke finde svaret du ledte efter? Tjek vores blog for dybdegående guides og tips, 
              eller kontakt direkte den forhandler hvis produkt du er interesseret i.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
            >
              Besøg Vores Blog
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      </main>
      <Footer />
    </div>
  );
}

